import React, { useCallback, useEffect, useMemo, useState } from "react";

export const unitListToFixedDigits = (unit?: string) => {
  switch (unit) {
    case "degC": // température
    case "m":
    case "t":
      return 1;
    case "V":
    case "m3/h/kg":
    case "m3/h/animal":
    case "g/m3":
      return 2;
    case "mV/V":
    case "kJ/kg":
    case "kW":
      return 3;
    case "L":
    case "m3":
    case "percent":
    case "deg": // unité des angles
      return 0;
    default:
      return 0;
  }
};

export const NumberInput = ({
  regulationValue,
  unit,
  showUnit = true,
  onChange,
  label,
  extraLabel = "",
  inline = false,
  isNullable = false,
  fixedDigits,
  showIncrementAndDecrement = true,
  isDisabled = false,
  classWidth = "w-64",
  parametersValues,
}: {
  regulationValue: number | undefined;
  label: string;
  onChange: (value: number) => void;
  parametersValues?: unknown;
  unit?: string;
  showUnit?: boolean;
  extraLabel?: string;
  inline?: boolean;
  isNullable?: boolean;
  fixedDigits?: number;
  showIncrementAndDecrement?: boolean;
  isDisabled?: boolean;
  classWidth?: string;
}) => {
  // Kept for backward compatibility with callers; no min/max constraint anymore.
  void parametersValues;

  const usableUnit = unit;
  const usableFixedDigits = fixedDigits ?? unitListToFixedDigits(usableUnit);
  const step = useMemo(() => 1 / 10 ** usableFixedDigits, [usableFixedDigits]);

  const formatValue = useCallback(
    (value?: number) => {
      if (typeof value !== "number" || Number.isNaN(value)) {
        return isNullable ? "" : (0).toFixed(usableFixedDigits);
      }
      return value.toFixed(usableFixedDigits);
    },
    [isNullable, usableFixedDigits]
  );

  const [inputValue, setInputValue] = useState<string>(
    formatValue(regulationValue)
  );

  useEffect(() => {
    setInputValue(formatValue(regulationValue));
  }, [formatValue, regulationValue]);

  const parseInput = useCallback((value: string) => {
    const normalized = value.replace(",", ".");
    const parsed = Number.parseFloat(normalized);
    return Number.isNaN(parsed) ? undefined : parsed;
  }, []);

  const handleNativeInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const nextRawValue = event.target.value;
      setInputValue(nextRawValue);

      if (nextRawValue === "") {
        return;
      }

      const parsed = parseInput(nextRawValue);
      if (typeof parsed === "number") {
        onChange(parsed);
      }
    },
    [onChange, parseInput]
  );

  const handleStepChange = useCallback(
    (direction: 1 | -1) => {
      const currentFromInput = parseInput(inputValue);
      const current =
        typeof currentFromInput === "number"
          ? currentFromInput
          : typeof regulationValue === "number"
            ? regulationValue
            : 0;

      const next = Number(
        (current + direction * step).toFixed(usableFixedDigits)
      );
      setInputValue(next.toFixed(usableFixedDigits));
      onChange(next);
    },
    [inputValue, onChange, parseInput, regulationValue, step, usableFixedDigits]
  );

  return (
    <div className="flex flex-row m-2">
      <div
        className={`flex ${
          inline
            ? `h-full w-full`
            : `rounded-xl border-2 ${
                isDisabled ? "border-grey-light" : "border-grey-medium"
              } ${classWidth} bg-grey-light/20`
        } ${isDisabled ? " cursor-not-allowed" : "cursor-pointer"}`}
      >
        {!inline && showIncrementAndDecrement && !isDisabled && (
          <button
            type="button"
            className="w-12 text-4xl font-medium text-grey-dark hover:bg-grey-light transition-colors rounded-l-lg"
            onClick={() => handleStepChange(-1)}
          >
            -
          </button>
        )}
        <div
          className={`fcen text-center text-${
            isDisabled ? "grey-medium" : "grey-dark"
          } flex-1 min-w-0
          ${
            usableUnit === "ppm" || usableUnit === "m3/h/kg"
              ? "text-2xl"
              : "text-3xl"
          }
          ${isDisabled ? "bg-grey-light" : inline ? "" : "bg-white"}`}
        >
          <div className="w-full flex items-center justify-center gap-2 px-2">
            <input
              aria-label={extraLabel ? `${label} ${extraLabel}` : label}
              className="[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none bg-transparent text-inherit text-center w-full outline-none text-2xl font-medium"
              disabled={isDisabled}
              inputMode="decimal"
              step={step}
              type="number"
              value={inputValue}
              onChange={handleNativeInputChange}
            />
            {showUnit && usableUnit && (
              <span className="text-base">{usableUnit}</span>
            )}
          </div>
        </div>
        {!inline && showIncrementAndDecrement && !isDisabled && (
          <button
            type="button"
            className="w-12 text-2xl font-medium text-grey-dark hover:bg-grey-light transition-colors rounded-r-lg"
            onClick={() => handleStepChange(1)}
          >
            +
          </button>
        )}
      </div>
    </div>
  );
};
